import React from 'react';
import Nav from 'react-bootstrap/Nav';
import { LinkContainer } from 'react-router-bootstrap';
import AccountSelect from 'components/ui/accountSelect/AccountSelect';
import WorkspaceSelect from 'components/ui/workspaceSelect/WorkspaceSelect';
import MixpanelService from 'utils/mixpanelService';
import { MOCK_SERVICE_APPLY_CONFIGURATION, OPEN_TOP_MENU_TAB, TAB_CHANGED } from 'utils/mixpanelEvents';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { BlazemeterUserConfig } from 'types/rest/blazeMeterApiResponse';
import { hasUserAdminRoles } from 'utils/bzmUserConfigUtil';
import BzmIcon from '../../../components/icons/BzmIcon';

const changeTab = (tabName: string) => {
  MixpanelService.track(TAB_CHANGED(tabName));
};

interface TopMenuProps {
  blazeUserConfig: BlazemeterUserConfig;
  isViewAs: number | null;
  viewAsYourself: () => void;
}

const TopMenu = (props: TopMenuProps) => {
  const userCanInvite = props.blazeUserConfig.user.canInvite;
  const { t } = useTranslation();

  return (
    <div className={clsx('nav-tabs-menu', props.isViewAs && 'nav-tabs-menu-view-as')}>
      <div className={'container-fluid'}>
        <div className={'d-flex'}>
          <div className={'nav nav-tabs mr-auto'}>
            <div className={'nav-item dropdown'}>
              <WorkspaceSelect />
            </div>

            <Nav.Item
              onClick={() => {
                MixpanelService.track(OPEN_TOP_MENU_TAB('mock-services'));
              }}
            >
              <LinkContainer onClick={() => changeTab('/mock-services')} to="/mock-services">
                <Nav.Link href="/mock-services" active={false}>
                  {t('topMenuTitles.mockServices')}
                </Nav.Link>
              </LinkContainer>
            </Nav.Item>

            <Nav.Item
              onClick={() => {
                MixpanelService.track(OPEN_TOP_MENU_TAB('asset-catalog'));
              }}
            >
              <LinkContainer onClick={() => changeTab('/asset-catalog')} to="/asset-catalog">
                <Nav.Link href="/asset-catalog" active={false}>
                  {t('topMenuTitles.assetCatalog')}
                </Nav.Link>
              </LinkContainer>
            </Nav.Item>

            <Nav.Item
              onClick={() => {
                MixpanelService.track(OPEN_TOP_MENU_TAB('analytics'));
              }}
            >
              <LinkContainer onClick={() => changeTab('/analytics')} to="/analytics">
                <Nav.Link href="/analytics" active={false}>
                  {t('topMenuTitles.analytics')}
                </Nav.Link>
              </LinkContainer>
            </Nav.Item>

            <Nav.Item
              onClick={() => {
                MixpanelService.track(OPEN_TOP_MENU_TAB('configurations'));
              }}
            >
              <LinkContainer onClick={() => changeTab('/configurations')} to="/configurations">
                <Nav.Link href="/configurations" active={false}>
                  {t('topMenuTitles.configurations')}
                </Nav.Link>
              </LinkContainer>
            </Nav.Item>

            <Nav.Item
              onClick={() => {
                MixpanelService.track(OPEN_TOP_MENU_TAB('learn-more'));
              }}
            >
              <LinkContainer onClick={() => changeTab('/learn-more')} to="/learn-more">
                <Nav.Link href="/learn-more" active={false}>
                  {t('topMenuTitles.learnMore')}
                </Nav.Link>
              </LinkContainer>
            </Nav.Item>
          </div>

          <div className={'nav nav-tabs flex-nowrap'}>
            {props.isViewAs && (
              <li className="nav-item">
                <button onClick={props.viewAsYourself} className="btn btn-warning mx-4">
                  {t('topMenuTitles.viewAsYourself')}
                </button>
              </li>
            )}
            {userCanInvite && (
              <div className={'nav-item d-inline-block'}>
                <a
                  className={'btn btn-link-dark btn-no-hover text-decoration-none'}
                  href={props.blazeUserConfig.config.inviteUrl}
                  aria-hidden="true"
                  aria-label={t('topMenuTitles.inviteNewMembers')}
                  title={t('topMenuTitles.inviteNewMembers')}
                >
                  <BzmIcon variant={'icon-bzm-user-plus'} className={'icon-sm'} />
                </a>
              </div>
            )}
            {hasUserAdminRoles(props.blazeUserConfig) && (
              <Nav.Item
                onClick={() => {
                  MixpanelService.track(OPEN_TOP_MENU_TAB('admin'));
                }}
              >
                <LinkContainer onClick={() => changeTab('/admin')} to="/admin">
                  <Nav.Link href="/admin" active={false}>
                    {t('topMenuTitles.admin')}
                  </Nav.Link>
                </LinkContainer>
              </Nav.Item>
            )}
            {props.blazeUserConfig.user.accounts && props.blazeUserConfig.user.accounts.length > 1 && (
              <div className={'nav-item dropdown'}>
                <AccountSelect />
              </div>
            )}
            <div className={'nav-item d-inline-block'}>
              <a
                className={'btn btn-link-dark btn-no-hover text-decoration-none'}
                href={props.blazeUserConfig.config.accountSettingsUrl}
                aria-hidden="true"
                aria-label={t('topMenuTitles.accountSettings')}
                title={t('topMenuTitles.accountSettings')}
              >
                <BzmIcon variant={'icon-bzm-settings'} className={'icon-sm'} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopMenu;
